import React from 'react';
import cn from 'classnames';
import { oneOf, node, func, string, object, bool } from 'prop-types';
import Toggler from '@fiverr/ui_toggle';
import getRenderable from '../../../lib/getRenderable';
import {
    BASE_CLASSES,
    TRIGGERS,
    POSITIONS,
    POSITIONS_OPPOSITES,
    UNSUPPORTED_POSITION_ERROR,
    UNSUPPORTED_TRIGGER_ERROR
} from './constants';
import './index.scss';

const isFunction = (fn) => typeof fn === 'function';

/**
 * @property {Object} popoverProps The relevant props passed to the component to support render props.
 * @property {Object} eventHandlers The events this popover responds to.
 * @property {String} position The popover's calculated position. Takes into consideration screen overflowing and will try to render the content in the opposite direction.
 */
class Popover extends Toggler {
    constructor(props) {
        super(props);

        /**
         * Merge the state with Toggler's base state, but first make sure "state" is an object.
         */

        this.state = this.state || {};
        Object.assign(this.state, {
            overflowing: false
        });
    }

    get popoverProps() {
        const {
            open,
            close,
            toggle,
            preventCloseAction
        } = this;

        const {
            isOpen
        } = this.state;

        const {
            trigger,
            children,
            ...props
        } = this.props;

        return Object.assign({
            open,
            close,
            toggle,
            preventCloseAction,
            isOpen
        }, props);
    }

    get eventHandlers() {
        const {
            trigger
        } = this.props;

        switch (trigger) {
            case TRIGGERS.CLICK:
                return {
                    onClick: this.toggle
                };
            case TRIGGERS.HOVER:
                return {
                    onMouseEnter: this.open,
                    onMouseLeave: this.close
                };
            case TRIGGERS.NONE:
                return {};
            default:
                throw new Error(UNSUPPORTED_TRIGGER_ERROR);
        }
    }

    get position() {
        const { overflowing } = this.state;
        const { position } = this.props;

        if (!Object.values(POSITIONS).includes(position)) {
            throw new Error(UNSUPPORTED_POSITION_ERROR);
        }

        if (!this.contentBox) {
            return position;
        }

        const [basePosition, modifier] = position.split('-');

        const newPosition = overflowing ? POSITIONS_OPPOSITES[basePosition] : basePosition;

        return [newPosition, modifier].filter(Boolean).join('-');
    }

    get shouldShowContent() {
        const { content } = this.props;
        const { isOpen } = this.state;

        return isOpen && [null, undefined].includes(content) === false;
    }

    fixOverflow() {
        const [position] = this.props.position.split('-');
        const rect = this.contentBox.getBoundingClientRect();
        const { innerWidth, innerHeight } = window;
        let overflowing = false;

        switch (position) {
            case POSITIONS.BOTTOM:
                overflowing = rect.bottom > innerHeight;
                break;
            case POSITIONS.TOP:
                overflowing = rect.top < 0;
                break;
            case POSITIONS.RIGHT:
                overflowing = rect.right > innerWidth;
                break;
            case POSITIONS.LEFT:
                overflowing = rect.left < 0;
                break;
            default:
                break;
        }

        this.setState({
            overflowing
        });
    }

    async togglerWillOpen(e) {
        const { willOpen } = this.props;

        if (isFunction(willOpen)) {
            await willOpen(e);
        }
    }

    togglerDidOpen(e) {
        const { onOpen, allowOverflow } = this.props;

        if (this.contentBox && !allowOverflow) { this.fixOverflow(); }

        if (isFunction(onOpen)) {
            onOpen(e);
        }
    }

    async togglerWillClose(e) {
        const { willClose } = this.props;

        if (isFunction(willClose)) {
            await willClose(e);
        }
    }

    togglerDidClose(e) {
        const { onClose } = this.props;

        this.setState({
            overflowing: false
        });

        if (isFunction(onClose)) {
            onClose(e);
        }
    }

    render() {
        const {
            content,
            children,
            trigger,
            className,
            contentClassName,
            boxClassName,
            boxContentClassName,
            style
        } = this.props;
        const { isOpen } = this.state;

        const containerClasses = cn(BASE_CLASSES, `fit-popover-${this.position}`, {
            'fit-popover-clickable': trigger === TRIGGERS.CLICK,
            'fit-popover-open': isOpen
        }, className);
        const contentClasses = cn('fit-popover-content', contentClassName);
        const boxClasses = cn('fit-popover-box', boxClassName);
        const boxContentClasses = cn('fit-popover-box-content', boxContentClassName);

        return (
            <div className={containerClasses}
                data-position={this.position}
                ref={this.preventCloseAction}>
                <span className={contentClasses}
                    ref={(ref) => this.target = ref}
                    {...this.eventHandlers}>
                    {getRenderable(children, this.popoverProps)}
                </span>
                {this.shouldShowContent && (
                    <aside className={boxClasses}
                        ref={(el) => this.contentBox = el}
                        style={style}>
                        <div className={boxContentClasses}>
                            {getRenderable(content)}
                        </div>
                    </aside>
                )}
            </div>
        );
    }
}

Popover.propTypes = {
    /**
     * The content that's being wrapped with the popover functionality.
     */
    children: node.isRequired,
    /**
     * The popover box's content.
     */
    content: node.isRequired,
    /**
     * The direction in which the popover box will open.
     * Supported options are exposed through the static `POSITIONS` property. | `POSITIONS.TOP`
     */
    position: oneOf(Object.values(POSITIONS)),
    /**
     * Whether or not an overflow is allowed.
     * If true, will preserve the original position even if the box overflows the screen.
     */
    allowOverflow: bool,
    /**
     * The triggering behaviour for the popover.
     * Supported options are exposed through the static `TRIGGERS` property.
     */
    trigger: oneOf(Object.values(TRIGGERS)),
    /**
     * Custom classes you wish to pass to the component.
     */
    className: string,
    /**
     * Custom classes you wish to pass to the component's content element (`.fit-popover-content`).
     */
    contentClassName: string,
    /**
     * Custom classes you wish to pass to the component's box element (`.fit-popover-box`).
     */
    boxClassName: string,
    /**
     * Custom classes you wish to pass to the component's box content element (`.fit-popover-box-content`).
     */
    boxContentClassName: string,
    /**
     * Custom styles you wish to pass to the component.
     */
    style: object,
    /**
     * A callback function triggered when the popover is about to be opened.
     * Receives one argument - the event that caused the open cycle.
     */
    willOpen: func,
    /**
     * A callback function triggered when the popover is about to be closed.
     * Receives one argument - the event that caused the close cycle.
     */
    willClose: func,
    /**
     * A callback function triggered when the popover is opened.
     * Receives one argument - the event that caused the open cycle.
     */
    onOpen: func,
    /**
     * A callback function triggered when the popover is closed.
     * Receives one argument - the event that caused the close cycle.
     */
    onClose: func
};

Popover.defaultProps = {
    position: POSITIONS.TOP,
    allowOverflow: false,
    trigger: TRIGGERS.CLICK,
    style: {}
};

Object.assign(Popover, {
    POSITIONS,
    TRIGGERS
});

export default Popover;
